import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHead, Lede, Card, Kpi, Note, Kv } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { QueryState } from "../../components/States";
import { isRetryable } from "../../api/errors";
import { num, pct, short } from "../../lib/format";
import {
  AUX_METRICS,
  PRIMARY_METRIC,
  fourClassCells,
  latestByMode,
  rateText,
  readParity,
  readParityDaily,
  readParityDay,
  readSameSourceRuns,
  type ParityPayload,
  type ParityStrategy,
} from "./api";

const READ = { retry: (n: number, e: unknown) => n < 2 && isRetryable(e), retryDelay: 1_500 } as const;
const MODES = ["回测", "纸上盘"];

export function ParityScreen(): ReactNode {
  const parity = useQuery({ queryKey: ["g3", "parity"], queryFn: readParity, ...READ });
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <>
      <PageHead title="三处同源比对" sub="GET /parity/crosscheck · someo-backtrader keel-crosscheck.json" />
      <Lede>
        同一段历史信号交给 keel 与独立回测引擎（Backtrader）各跑一遍：主指标是<b>订单序列一致率</b>（order_sequence_match_rate），差异按产物给出的分类呈现；
        操作台只读产物、不重算比率、不重新归类。
      </Lede>
      <QueryState status={parity.status} error={parity.error} what="跨引擎交叉核对（GET /parity/crosscheck，execution-kernel「跨引擎交叉核对」）" refetch={() => void parity.refetch()}>
        {parity.status === "success" ? <Crosscheck p={parity.data} picked={picked ?? parity.data.strategies[0]?.key ?? null} onPick={setPicked} /> : null}
      </QueryState>
      <div style={{ marginTop: 12 }}>
        <SameSource />
      </div>
    </>
  );
}

function Crosscheck({ p, picked, onPick }: { p: ParityPayload; picked: string | null; onPick: (k: string) => void }): ReactNode {
  const src = p.source;
  const s = p.strategies.find((x) => x.key === picked) ?? null;
  return (
    <>
      <Kv
        pairs={[
          ["产物", <span className="mono">{p.path}</span>],
          ["版本 / 摘要", `schema ${p.schema_version} · sha256 ${short(p.file_sha256, 12)} · ${p.file_mtime_utc}`],
          ["执行时点", `信号起 ${String(src["signal_start"] ?? "—")}，纸上盘起 ${String(src["paper_start"] ?? "—")}（信号日的下一个交易日开盘撮合），止 ${String(src["end"] ?? "—")}`],
          ["主指标", <code>{p.primary_metric}</code>],
        ]}
      />
      <Card title="一致率（按策略）" sub="点一行看差异分类与逐日对照" nopad>
        <Table
          empty="产物里没有策略"
          cols={[
            { label: "策略" },
            { label: "订单 BT / keel / 分母", num: true },
            { label: "订单序列一致率（主）", num: true },
            { label: "多重集合", num: true },
            { label: "标的+方向序列", num: true },
            { label: "标的+方向集合", num: true },
            { label: "最大权益相对偏差", num: true },
            { label: "状态" },
          ]}
          rows={p.strategies.map((x) => ({
            key: x.key,
            selected: x.key === picked,
            onClick: () => onPick(x.key),
            cells: [
              <>{x.key} <span className="faint">{x.strategy_id ?? ""}</span></>,
              `${x.backtrader_orders ?? "—"} / ${x.keel_orders ?? "—"} / ${x.comparison_denominator ?? "—"}`,
              <b>{rateText(x.rates[PRIMARY_METRIC] ?? null, x.comparison_denominator)}</b>,
              ...AUX_METRICS.map((m) => rateText(x.rates[m] ?? null, x.comparison_denominator)),
              pct(x.max_equity_relative_deviation),
              x.halted ? <Pill tone="halt">核对运行中停机：{x.halt_reason ?? "原因未给"}</Pill> : <Pill tone="ok">未停机</Pill>,
            ],
          }))}
        />
      </Card>
      {s ? <Detail p={p} s={s} /> : null}
      <details style={{ marginTop: 12 }}>
        <summary>口径说明（产物 method 原文）</summary>
        <Kv pairs={Object.entries(p.method).map(([k, v]) => [k, v] as [string, string])} />
      </details>
    </>
  );
}

function Detail({ p, s }: { p: ParityPayload; s: ParityStrategy }): ReactNode {
  const four = fourClassCells(p.four_class_labels, s.four_class);
  return (
    <>
      <div className="grid g4" style={{ marginTop: 12 }}>
        {four.map((c) => (
          <Kpi key={c.label} label={`差异四类 · ${c.label}`} value={c.value === "缺" ? <span className="faint">缺</span> : c.value} />
        ))}
      </div>
      {s.four_class === null ? (
        <Note tone="warn">产物未给四类归属字段（four_class_attribution，待 someo-backtrader 补，裁决 B5）：四类标「缺」，操作台不从下表八类自行归类。</Note>
      ) : null}
      <Card title={`差异分类 · ${s.key}（产物原样）`} sub={`最大成交价差 ${num(s.max_fill_price_abs_diff, 3)} · 最大手续费差 ${num(s.max_commission_abs_diff, 3)}`} nopad>
        {s.categories === null ? (
          <div className="empty">产物未给差异分类</div>
        ) : (
          <Table
            cols={[{ label: "类别" }, { label: "计数", num: true }, { label: "产物解释", wrap: true }]}
            rows={Object.entries(s.categories).map(([k, v]) => ({ key: k, cells: [k, v, p.category_explanations[k] ?? "—"] }))}
          />
        )}
      </Card>
      <DailyCompare strategy={s.key} />
    </>
  );
}

function DailyCompare({ strategy }: { strategy: string }): ReactNode {
  const daily = useQuery({ queryKey: ["g3", "parity-daily", strategy], queryFn: () => readParityDaily(strategy), ...READ });
  const [day, setDay] = useState<string | null>(null);
  const orders = useQuery({ queryKey: ["g3", "parity-day", strategy, day], queryFn: () => readParityDay(strategy, day as string), enabled: Boolean(day), ...READ });
  return (
    <div style={{ marginTop: 12 }}>
      <Card title={`逐日对照 · ${strategy}`} sub="GET /parity/crosscheck/daily · 点一行看逐笔订单" nopad>
        <QueryState status={daily.status} error={daily.error} what="逐日对照" refetch={() => void daily.refetch()}>
          {daily.status === "success" ? (
            <Table
              empty="产物里没有逐日数据"
              cols={[{ label: "日期" }, { label: "BT 订单", num: true }, { label: "keel 订单", num: true }, { label: "序列匹配", num: true }, { label: "集合匹配", num: true }, { label: "权益差", num: true }]}
              rows={daily.data.map((d) => ({
                key: d.day,
                selected: d.day === day,
                onClick: () => setDay(d.day),
                cells:
                  d.backtrader_orders === 0 && d.keel_orders === 0
                    ? [d.day, "当日两边均无订单", "", "", "", num(d.equity_abs_diff)]
                    : [d.day, d.backtrader_orders ?? "—", d.keel_orders ?? "—", d.sequence_matches ?? "—", d.multiset_matches ?? "—", num(d.equity_abs_diff)],
              }))}
            />
          ) : null}
        </QueryState>
      </Card>
      {day ? (
        <div style={{ marginTop: 12 }}>
          <QueryState status={orders.status} error={orders.error} what={`${day} 逐笔订单`} refetch={() => void orders.refetch()}>
            {orders.status === "success" ? (
              <div className="grid g2">
                <OrderTable title={`Backtrader · ${day}`} rows={orders.data.backtrader} cols={["symbol", "side", "shares", "price", "commission"]} />
                <OrderTable title={`keel · ${day}`} rows={orders.data.keel} cols={["symbol", "side", "shares", "status", "price", "raw_price", "commission"]} />
              </div>
            ) : null}
          </QueryState>
        </div>
      ) : null}
    </div>
  );
}

function OrderTable({ title, rows, cols }: { title: string; rows: Record<string, unknown>[]; cols: string[] }): ReactNode {
  return (
    <Card title={title} nopad>
      <Table
        empty="当日无订单"
        cols={cols.map((c) => ({ label: c, num: ["shares", "price", "raw_price", "commission"].includes(c) }))}
        rows={rows.map((r, i) => ({ key: `${i}`, cells: cols.map((c) => (typeof r[c] === "number" ? num(r[c] as number, c === "shares" ? 0 : 3) : String(r[c] ?? "—"))) }))}
      />
    </Card>
  );
}

function SameSource(): ReactNode {
  const runs = useQuery({ queryKey: ["g3", "same-source-runs"], queryFn: readSameSourceRuns, ...READ });
  return (
    <Card title="三处同源" sub="GET /runs · 只并排原值，不判定一致与否" nopad>
      <QueryState status={runs.status} error={runs.error} what="运行索引（GET /runs）" refetch={() => void runs.refetch()}>
        {runs.status === "success"
          ? (() => {
              const latest = latestByMode(runs.data, MODES);
              const cell = (mode: string, pick: (r: NonNullable<(typeof latest)[string]>) => ReactNode): ReactNode =>
                latest[mode] ? pick(latest[mode]!) : <span className="faint">还没有{mode}运行</span>;
              const field = (label: string, pick: (r: NonNullable<(typeof latest)[string]>) => ReactNode) => ({
                key: label,
                cells: [label, ...MODES.map((m) => cell(m, pick)), <span className="faint">实盘：未接通道</span>],
              });
              return (
                <Table
                  cols={[{ label: "项" }, ...MODES.map((m) => ({ label: m, mono: true })), { label: "实盘" }]}
                  rows={[
                    field("run_id", (r) => <a href={`#/replay?run=${encodeURIComponent(r.run_id)}`}>{r.run_id}</a>),
                    field("策略 / 交易日", (r) => `${r.strategy_id ?? "—"} / ${r.trade_date ?? "—"}`),
                    field("config_digest", (r) => short(r.config_digest, 12)),
                    field("snapshot_id", (r) => short(r.snapshot_id, 16)),
                    field("ledger_digest", (r) => short(r.ledger_digest, 12)),
                    field("订单 / 成交", (r) => `${r.orders ?? "—"} / ${r.fills ?? "—"}`),
                  ]}
                />
              );
            })()
          : null}
      </QueryState>
    </Card>
  );
}
